import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, ArrowRight, Loader2, CreditCard } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useStripeInvoices } from "@/providers/StripeInvoiceProvider";
import type { InvoiceDto } from "@shared/types/InvoiceDto";
import { cn } from "@/lib/utils";

function getDaysUntilDue(invoice: InvoiceDto): number {
  const createdDate = new Date(invoice.createdAt);
  const dueDate = new Date(createdDate);
  dueDate.setDate(dueDate.getDate() + 30);
  const daysUntil = Math.ceil((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return daysUntil;
}

function getDueLabel(daysUntil: number): string {
  if (daysUntil <= 0) return "Due today";
  if (daysUntil === 1) return "Due tomorrow";
  return `Due in ${daysUntil} days`;
}

function getDueColorClass(daysUntil: number): string {
  if (daysUntil <= 0) return "bg-red-500/15 text-red-400 border-red-500/30";
  if (daysUntil <= 3) return "bg-red-500/10 text-red-400 border-red-500/20";
  if (daysUntil <= 7) return "bg-amber-500/15 text-amber-400 border-amber-500/30";
  return "bg-amber-500/10 text-amber-400 border-amber-500/20";
}

function InvoiceRow({ invoice }: { invoice: InvoiceDto }) {
  const { downloadInvoice, isDownloading } = useStripeInvoices();
  const daysUntil = getDaysUntilDue(invoice);
  const isPayingThis = isDownloading === invoice.id;

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
          <Receipt className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{invoice.name}</p>
          <p className="text-xs text-muted-foreground">
            ${(invoice.amount / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold", getDueColorClass(daysUntil))}>
          {getDueLabel(daysUntil)}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => downloadInvoice(invoice.id)}
          disabled={!!isPayingThis}
          className="h-7 text-xs gap-1"
        >
          {isPayingThis ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" />}
          Pay now
        </Button>
      </div>
    </div>
  );
}

export function InvoicesCard() {
  const navigate = useNavigate();
  const { invoices, isLoading, fetchInvoices } = useStripeInvoices();
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (!hasFetched) {
      fetchInvoices();
      setHasFetched(true);
    }
  }, [fetchInvoices, hasFetched]);

  const upcomingInvoices = invoices
    .filter((inv) => inv.status !== "PAID")
    .filter((inv) => getDaysUntilDue(inv) <= 10)
    .sort((a, b) => getDaysUntilDue(a) - getDaysUntilDue(b));

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-base font-semibold">Upcoming Invoices</CardTitle>
        <Button variant="ghost" size="sm" onClick={() => navigate("/invoices")} className="text-xs">
          View all <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : upcomingInvoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
              <Receipt className="h-6 w-6 text-emerald-400" />
            </div>
            <p className="text-sm text-muted-foreground">No upcoming invoices</p>
          </div>
        ) : (
          <div className="space-y-1">
            {upcomingInvoices.slice(0, 5).map((invoice) => (
              <InvoiceRow key={invoice.id} invoice={invoice} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
